package co.uk.paxes.pinpoint.web

import kotlinx.browser.document
import kotlinx.browser.window
import co.uk.paxes.pinpoint.web.platform.PlatformEnvironment

/**
 * BannerAdManager handles banner ad display.
 * - PlayGama: Uses bridge for sticky banner ads
 * - Web/Other: Uses Google AdSense Auto Ads
 * 
 * Requirements: 9.2, 9.3, 9.4
 */
object BannerAdManager {
    
    private var isInitialized = false
    private var bannerDisplayed = false
    
    /**
     * Initialize the ad manager.
     * Should be called on app startup.
     */
    fun initialize() {
        if (isInitialized) return
        
        console.log("BannerAdManager: Initializing...")
        
        val env = try {
            PlatformEnvironment.getEnvironment()
        } catch (e: Exception) {
            PlatformEnvironment.Environment.STANDALONE
        }
        
        // PlayGama: Show banner via bridge
        if (env == PlatformEnvironment.Environment.PLAYGAMA) {
            console.log("BannerAdManager: PlayGama detected - will use bridge for banner ads")
            showPlayGamaBanner()
        } else {
            // Web/Other: Use AdSense
            val adsbygoogle = js("window.adsbygoogle")
            if (adsbygoogle != null) {
                console.log("BannerAdManager: AdSense SDK detected")
            } else {
                console.log("BannerAdManager: AdSense SDK not available (may be blocked by ad blocker)")
            }
            
            // Ensure container is visible
            showBannerAd()
        }
        
        isInitialized = true
    }
    
    /**
     * Show PlayGama sticky banner ad via bridge.
     */
    private fun showPlayGamaBanner() {
        try {
            val bridge: dynamic = js("window.bridge")
            val advertisement = bridge?.advertisement
            
            if (advertisement != null && js("typeof advertisement.showBanner === 'function'") as Boolean) {
                console.log("📺 BannerAdManager: Showing PlayGama sticky banner")
                try {
                    advertisement.showBanner()
                    bannerDisplayed = true
                    console.log("✅ BannerAdManager: PlayGama banner shown")
                } catch (e: Exception) {
                    console.warn("⚠️ BannerAdManager: Error showing PlayGama banner: ${e.message}")
                }
            } else {
                console.warn("⚠️ BannerAdManager: bridge.advertisement.showBanner not available")
            }
        } catch (e: Exception) {
            console.warn("⚠️ BannerAdManager: Error accessing PlayGama bridge: ${e.message}")
        }
    }
    
    /**
     * Show the banner ad at the bottom of the screen.
     * The banner is positioned in a non-intrusive location.
     * 
     * Requirements: 9.2, 9.4
     */
    fun showBannerAd() {
        val container = document.getElementById("banner-ad-container")
        if (container != null) {
            container.asDynamic().style.display = "flex"
            console.log("BannerAdManager: Banner ad container shown")
        }
    }
    
    /**
     * Hide the banner ad (e.g., during fullscreen game moments).
     */
    fun hideBannerAd() {
        val container = document.getElementById("banner-ad-container")
        if (container != null) {
            container.asDynamic().style.display = "none"
            console.log("BannerAdManager: Banner ad container hidden")
        }
    }
    
    /**
     * Show an interstitial ad (e.g., on game end).
     * For PlayGama, uses the bridge API with state detection.
     * For web, shows PropellerAds Direct Link.
     * 
     * Requirements: 9.3
     * 
     * @param placement Ad placement identifier (e.g., "game_over", "hand_4", "hand_8")
     * @param onAdShowing Callback when ad actually shows (pause game here)
     * @param onClose Callback when ad closes (resume game here)
     */
    fun showInterstitialAd(placement: String = "interstitial_game_over", onAdShowing: () -> Unit = {}, onClose: () -> Unit) {
        try {
            val env = PlatformEnvironment.getEnvironment()
            
            if (env == PlatformEnvironment.Environment.PLAYGAMA) {
                console.log("📺 BannerAdManager: Showing PlayGama interstitial (placement: $placement)")
                showInterstitialAdViaBridge(placement, onAdShowing, onClose)
                return
            }
        } catch (e: Exception) {
            console.warn("⚠️ BannerAdManager: Error checking environment: ${e.message}")
        }
        
        // Web/Other: Show PropellerAds interstitial in new tab
        console.log("📺 BannerAdManager: Showing web interstitial (PropellerAds)")
        try {
            val interstitialUrl = "https://omg10.com/4/10880682"
            val adWindow = window.open(interstitialUrl, "_blank")
            
            if (adWindow == null) {
                console.warn("⚠️ BannerAdManager: Popup blocked")
                val message = """
                    🎯 Ad Blocked!
                    
                    Please allow popups for this site to support the game.
                    
                    Click the popup blocker icon in your browser's address bar and allow popups from pinpoint-1978d.web.app
                """.trimIndent()
                window.alert(message)
                onClose()
                return
            }
            
            console.log("✅ BannerAdManager: Web interstitial opened")
            onAdShowing()  // Pause for web ads too
            window.setTimeout({
                onClose()
            }, 500)
        } catch (e: Exception) {
            console.warn("⚠️ BannerAdManager: Failed to open interstitial: ${e.message}")
            onClose()
        }
    }
    
    /**
     * Show interstitial ad using PlayGama bridge API with state detection.
     * Detects when ad is "showing" (pause) and "closed" (resume).
     */
    private fun showInterstitialAdViaBridge(placement: String, onAdShowing: () -> Unit, onClose: () -> Unit) {
        try {
            val bridge: dynamic = js("window.bridge")
            
            if (bridge == null || bridge.advertisement == null) {
                console.warn("⚠️ BannerAdManager: PlayGama bridge not available")
                onClose()
                return
            }
            
            console.log("📺 BannerAdManager: Using bridge.advertisement.showInterstitial()")
            
            var adHandled = false
            var adShown = false
            var stateHandler: ((dynamic) -> Unit)? = null
            
            val eventName: dynamic = bridge.EVENT_NAME?.INTERSTITIAL_STATE_CHANGED ?: "INTERSTITIAL_STATE_CHANGED"
            
            stateHandler = { state: dynamic ->
                val stateStr = state as? String ?: state.toString()
                console.log("📺 BannerAdManager: Interstitial state: $stateStr")
                
                // When ad opens/shows, pause game (states: "opened", "showing")
                if ((stateStr == "opened" || stateStr == "showing") && !adShown) {
                    adShown = true
                    console.log("📺 BannerAdManager: Ad OPENED/SHOWING - pausing game")
                    onAdShowing()
                }
                
                // When ad closes, resume game
                if ((stateStr == "closed" || stateStr == "finished") && !adHandled) {
                    adHandled = true
                    console.log("✅ BannerAdManager: Interstitial closed - resuming game")
                    
                    if (stateHandler != null) {
                        try {
                            bridge.advertisement.off(eventName, stateHandler)
                        } catch (e: Exception) {
                            // ignore
                        }
                    }
                    
                    onClose()
                }
            }
            
            bridge.advertisement.on(eventName, stateHandler)
            console.log("📺 BannerAdManager: Subscribed to INTERSTITIAL_STATE_CHANGED")
            
            bridge.advertisement.showInterstitial(placement)
            console.log("📺 BannerAdManager: Interstitial triggered")
            
        } catch (e: Exception) {
            console.warn("⚠️ BannerAdManager: Error: ${e.message}")
            onClose()
        }
    }
    
    /**
     * Close the interstitial ad programmatically.
     * With Auto Ads, we cannot programmatically close ads, so this is a no-op.
     */
    fun closeInterstitialAd() {
        console.log("BannerAdManager: closeInterstitialAd called (no-op)")
    }
    
    /**
     * Check if ads are available (not blocked).
     */
    fun isAdsAvailable(): Boolean {
        val adsbygoogle = js("window.adsbygoogle")
        return adsbygoogle != null
    }
}
